import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { QuoteTotalStrip } from '@/components/QuoteTotalStrip';
import { useNavigate } from 'react-router-dom';
import { Loader2, RefreshCw, Printer, Paintbrush, AlertTriangle, CheckCircle2, Upload, Plus, Trash2, ExternalLink, Info } from 'lucide-react';
import { toast } from 'sonner';
import { CalcShadowProvider, CalcShadowToggle, CalcShadow } from '@/components/CalcShadow';
import BuilderRoofDetailsPanel from '@/components/BuilderRoofDetailsPanel';
import { RoofM2ConfidenceBanner } from '@/components/RoofM2ConfidenceBanner';
import SaveDocButton from './SaveDocButton';

/**
 * PaintingBuilderTab — Iter158 Phase B.
 *
 * Standalone paint-only quote. Operator picks a Paint System (from the
 * seeded `paint_systems` catalogue) + roof_m² and we emit one line per
 * non-optional coat. Mirrors Stage-2 of the legacy Restoration builder
 * so the math stays consistent — paint sold standalone here OR rolled
 * into a Restoration package via the Combiner uses the same numbers.
 *
 * Endpoints:
 *   - GET  /api/painting/appraisals/{id}/preview
 *   - POST /api/painting/appraisals/{id}/push
 *   - GET  /api/painting/appraisals/{id}/quote.pdf
 */
export default function PaintingBuilderTab({ api, appraisalId, appraisal, onRefresh }) {
  const [bundle, setBundle] = useState(null);
  const [paintSystems, setPaintSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);
  const [paintSystemId, setPaintSystemId] = useState(appraisal?.restoration_paint_system_id || '');
  const [roofM2, setRoofM2] = useState(appraisal?.restoration_roof_m2 || 0);
  // iter313c H2/H3 fix — gate the "auto-snap roof m² from preview" so it
  // fires ONCE on first mount only. Without this, every keystroke in the
  // roof m² input recreates `load` (was in deps) which re-runs the mount
  // useEffect and snaps the value back mid-type. See form_audit_per_page.md.
  const didInitRef = React.useRef(false);
  // Iter218 — track the property's roof_details.roof_m² so the operator
  // can see the auto-source value next to the input + one-click sync
  // back to it after an override. Surfaced in the bundle when the
  // backend computes the fallback, but also fetched directly so the
  // "Sync from Roof Details" pill shows the right number even when
  // restoration_roof_m2 was already overridden.
  const [roofDetailsM2, setRoofDetailsM2] = useState(0);
  // Iter219 — Extra Painting catalog + operator's per-appraisal picks
  // (Recoat Fascia / Garage Doors / Gutters). Picks are persisted via
  // PUT /painting/appraisals/{id}/extras, then re-loaded with the
  // preview so the summary card includes them.
  const [extrasCatalog, setExtrasCatalog] = useState([]);
  const [extrasPicks, setExtrasPicks] = useState(appraisal?.restoration_extra_painting_items || []);
  const [savingExtras, setSavingExtras] = useState(false);
  // Iter231 (A5027 Issue 2) — collective gutter / barge / fascia
  // lengths read from the appraisal's pushed component_lines. Used
  // to pre-fill the per-row fascia + gutter editors so the operator
  // doesn't have to re-measure.
  const [extrasPrefill, setExtrasPrefill] = useState(null);
  // Iter231 — per-pick expand/collapse state for the detail editor.
  const [expandedExtras, setExpandedExtras] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const subtype = appraisal?.roof_subtype;
      // Iter218 — backend expects `?roof_subtype=…`, not `?subtype=…`
      // (this was a silent bug — the filter always failed open and
      // showed the operator every paint system in the catalogue
      // instead of the ones matching their roof subtype).
      // Iter317 · Batch L (2026-02 · A999421 · Bob) — Always pass
      // `appraisal_id` so the backend can derive the cascade key
      // from the property's roof_type when `roof_subtype` on the
      // appraisal is empty or non-canonical (e.g. "Rosscrete Hacienda"
      // → matches nothing in `_subtype_paint_match`'s canonical set).
      // Without this, the FE previously showed the FULL admin
      // catalogue → operator couldn't pick → Extras section stayed
      // hidden (line 486-491 gates on system-selected).
      const params = new URLSearchParams();
      if (subtype) params.set('roof_subtype', subtype);
      if (appraisalId) params.set('appraisal_id', appraisalId);
      const subQuery = params.toString() ? `?${params.toString()}` : '';
      const [previewRes, psRes, extrasRes, prefillRes] = await Promise.all([
        api.get(`/painting/appraisals/${appraisalId}/preview`),
        api.get(`/paint-systems${subQuery}`).catch(() => ({ data: [] })),
        // Extras catalogue = Component Rate Tables → Re-Coat rows (/each, /lm).
        api.get('/painting/extras-catalogue').catch(() => ({ data: [] })),
        // Iter231 — fetch the gutter / barge / fascia prefill sums
        // so newly-added fascia / gutter picks open with the right
        // default length pre-filled.
        api.get(`/appraisals/${appraisalId}/extra-painting-prefill`).catch(() => ({ data: null })),
      ]);
      setBundle(previewRes.data);
      setPaintSystems(psRes.data || []);
      setExtrasCatalog(extrasRes.data || []);
      setExtrasPrefill(prefillRes.data || null);
      const meta = previewRes.data?.meta || {};
      if (meta.paint_system_id) setPaintSystemId(meta.paint_system_id);
      const m2 = previewRes.data?.summary?.roof_m2;
      const m2Source = previewRes.data?.summary?.roof_m2_source || '';
      // Iter218 + iter313c H2 fix — on first mount only, snap the
      // roof m² field to the preview fallback. `didInitRef` prevents
      // subsequent keystroke-triggered reloads from reverting the
      // operator's edit mid-type.
      //
      // Iter317 · Batch K (2026-02 · A999421 · Bob) — When the preview
      // reports the m² came from `face_pins` (backend authority path
      // — sum of face-pin areas from the annotator), that value is
      // AUTHORITATIVE and MUST win over any stale `restoration_roof_m2`
      // seed. Previously the sync was gated on `!roofM2` and 255.9
      // (stale operator override) blocked the sync → operator saw
      // 255.9 instead of the correct 414 from face pin sum. Also:
      // when source is face_pins we intentionally SKIP the PUT that
      // persists the value onto the appraisal doc — writing a stale
      // copy of a diagram-derived value creates the exact problem
      // this fix is undoing. The diagram remains the source of truth.
      if (m2 && !didInitRef.current) {
        if (m2Source === 'face_pins') {
          // Diagram wins — force-sync regardless of prior state.
          if (Number(m2) !== Number(roofM2)) setRoofM2(m2);
          // No PUT — face-pin sum recomputes every load from the diagram.
        } else if (!roofM2) {
          // Non-face-pin source — legacy first-mount snap behaviour.
          setRoofM2(m2);
          if (!appraisal?.restoration_roof_m2) {
            try {
              // Wave 2 H9 fix — partial PUT (was spreading full appraisal)
              await api.put(`/appraisals/${appraisalId}`, {
                restoration_roof_m2: Number(m2) || 0,
              });
            } catch (_) { /* non-fatal: backend will recompute next time */ }
          }
        }
        didInitRef.current = true;
      }
      // Iter218 — pull the property's roof_details sum so the
      // "Sync from Roof Details" pill always knows the source value.
      if (appraisal?.property_id) {
        try {
          const { data: prop } = await api.get(`/properties/${appraisal.property_id}`);
          const total = (prop?.roof_details || []).reduce(
            (acc, r) => acc + (Number(r.roof_m2) || 0), 0,
          );
          setRoofDetailsM2(Math.round(total * 100) / 100);
        } catch (_) { /* non-fatal */ }
      }
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  // iter313c H3 fix — depend on stable primitive fields, NOT the whole
  // `appraisal` object (which is a fresh reference on every parent
  // render → reload loop). `roofM2` is intentionally NOT in deps
  // (iter313c H2 fix — see `didInitRef` above).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, appraisalId, appraisal?.roof_subtype, appraisal?.property_id, appraisal?.restoration_roof_m2, appraisal?.restoration_paint_system_id]);

  useEffect(() => { load(); }, [load]);

  const savePicks = async (nextSystemId, nextRoofM2) => {
    setSaving(true);
    try {
      // Wave 2 H9 fix — partial PUT (was spreading full appraisal)
      await api.put(`/appraisals/${appraisalId}`, {
        restoration_paint_system_id: nextSystemId || '',
        restoration_roof_m2: Number(nextRoofM2) || 0,
      });
      // iter313c post-verify LOW fix — drop the explicit `await load()`.
      // onRefresh() bubbles the fresh appraisal to the parent; the
      // primitive `appraisal.*` deps on `load` (see line ~113) fire the
      // useEffect which reloads once. Was double-fetching.
      if (onRefresh) onRefresh();
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const onSystemChange = async (id) => {
    setPaintSystemId(id);
    await savePicks(id, roofM2);
    // Auto-pick the matching Re-Coat quote letter (operator can change it).
    try { await api.put(`/painting/appraisals/${appraisalId}/quote-letter`, { auto: true }); await load(); }
    catch (_) { /* non-fatal */ }
  };
  const [letterOptions, setLetterOptions] = useState([]);
  useEffect(() => {
    api.get('/painting/quote-letter-options').then(r => setLetterOptions(r.data || [])).catch(() => {});
  }, [api]);
  const onLetterChange = async (ref) => {
    try {
      await api.put(`/painting/appraisals/${appraisalId}/quote-letter`, { quotation_type: ref });
      toast.success(`Quote letter set to ${ref}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };
  const onRoofM2Blur = () => {
    savePicks(paintSystemId, roofM2);
  };

  const onPush = async () => {
    if (!window.confirm('Push these paint lines onto the Components tab? Existing painting-builder lines will be replaced.')) return;
    setPushing(true);
    setFlash(null);
    try {
      const { data } = await api.post(`/painting/appraisals/${appraisalId}/push`);
      setFlash({ tone: 'ok', msg: `Pushed ${data.inserted} paint line${data.inserted === 1 ? '' : 's'} (grand total $${data.summary.grand_total.toFixed(2)})` });
      if (onRefresh) onRefresh();
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    } finally {
      setPushing(false);
    }
  };

  // Paint Quote PDF now routes through the canonical Document Auto-Save
  // system (SaveDocButton with docKind="painting_quotation") so the file
  // lands in the operator's download folder with the Access-style filename
  // instead of opening in a new tab. The painting-specific PDF content
  // (coat/qty/unit/rate/total + paint system notes) is unchanged — the
  // backend still dispatches to the painting_builder.painting_quote_pdf
  // generator via services/document_auto_archive.py.

  // Iter219 — Extras CRUD helpers. Local-first (so the UI feels snappy)
  // then PUT + reload. PUT replaces the whole array so deletes work.
  const saveExtras = async (next) => {
    setExtrasPicks(next);
    setSavingExtras(true);
    try {
      await api.put(`/painting/appraisals/${appraisalId}/extras`, { items: next });
      await load();
    } catch (e) {
      toast.error(`Extras save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSavingExtras(false);
    }
  };
  const addExtra = (itemId) => {
    if (!itemId) return;
    if (extrasPicks.find(p => p.item_id === itemId)) {
      toast.info('Already added — adjust qty below.');
      return;
    }
    // Iter231 (A5027 Issue 2) — seed default measurements from the
    // catalogue item's `default_*` fields + the appraisal's prefill
    // sums so the editor opens with sensible numbers the operator
    // can tweak rather than starting blank.
    const cat = (extrasCatalog || []).find(c => (c._id || c.id) === itemId) || {};
    const kind = cat.kind || 'generic';
    const measurements = {};
    if (kind === 'fascia') {
      measurements.length_m = extrasPrefill?.fascia_total_m || 0;
      measurements.depth_m  = cat.default_depth_m || 0.18;
    } else if (kind === 'gutter') {
      measurements.length_m = extrasPrefill?.gutter_total_m || 0;
      measurements.depth_m  = cat.default_depth_m || 0.12;
    } else if (kind === 'fence') {
      measurements.length_m = 0;
      measurements.depth_m  = cat.default_depth_m || 1.8;
    } else if (kind === 'garage_door_double' || kind === 'garage_door_small') {
      measurements.width_m  = cat.default_width_m  || 0;
      measurements.height_m = cat.default_height_m || 0;
    }
    saveExtras([...extrasPicks, { item_id: itemId, qty: 1, measurements }]);
    // Auto-expand the new row so the operator sees the editor.
    setExpandedExtras(prev => new Set(prev).add(itemId));
  };
  const updateExtraQty = (itemId, qty) => {
    saveExtras(extrasPicks.map(p => p.item_id === itemId ? { ...p, qty } : p));
  };
  // Iter231 — partial-merge measurement updates so editing one field
  // doesn't blank out the others.
  const updateExtraMeasurements = (itemId, patch) => {
    saveExtras(extrasPicks.map(p =>
      p.item_id === itemId
        ? { ...p, measurements: { ...(p.measurements || {}), ...patch } }
        : p
    ));
  };
  const toggleExtraExpanded = (itemId) => {
    setExpandedExtras(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      return next;
    });
  };
  const removeExtra = (itemId) => {
    saveExtras(extrasPicks.filter(p => p.item_id !== itemId));
  };

  // Iter314 R5 · Add Extra Line modal state. `null` = closed;
  // `{}` = open (empty form). Extended `addExtra` variant that accepts
  // an optional pin_id + origin so the modal can attach the extra to
  // a specific Face pin at add-time.
  const [addExtraModalOpen, setAddExtraModalOpen] = useState(false);

  // Iter314 C2 + C3 · Labour Extras state. Rows persist to
  // `restoration_paint_labour_extras`; the FE mirror stays in sync via
  // the preview endpoint's `labour_extras` response block. `laborCtx`
  // = misc labourer rate + auto-pulled barge lm from the diagram.
  const [labourExtras, setLabourExtras] = useState(
    appraisal?.restoration_paint_labour_extras || []
  );
  const [laborCtx, setLaborCtx] = useState({ labourer_rate: 0, barge_lm_total: 0, type_defaults: {} });
  const [labourSaving, setLabourSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/painting/appraisals/${appraisalId}/labour-extras-context`);
        setLaborCtx(r.data || {});
      } catch (e) { /* non-blocking — FE just shows 0 defaults */ }
    })();
  }, [api, appraisalId]);
  const saveLabourExtras = useCallback(async (next) => {
    setLabourExtras(next);
    setLabourSaving(true);
    try {
      await api.put(`/painting/appraisals/${appraisalId}/labour-extras`, { items: next });
      await load();
    } catch (e) {
      toast.error(`Labour extras save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLabourSaving(false);
    }
  }, [api, appraisalId, load]);
  const addExtraWithLink = (itemId, extraFields = {}) => {
    if (!itemId) return;
    if (extrasPicks.find(p => p.item_id === itemId
        && (p.pin_id || null) === (extraFields.pin_id || null))) {
      toast.info('Already added for that pin — adjust qty below.');
      return;
    }
    const cat = (extrasCatalog || []).find(c => (c._id || c.id) === itemId) || {};
    const kind = cat.kind || 'generic';
    const measurements = {};
    if (kind === 'fascia') {
      measurements.length_m = extrasPrefill?.fascia_total_m || 0;
      measurements.depth_m  = cat.default_depth_m || 0.18;
    } else if (kind === 'gutter') {
      measurements.length_m = extrasPrefill?.gutter_total_m || 0;
      measurements.depth_m  = cat.default_depth_m || 0.12;
    } else if (kind === 'fence') {
      measurements.length_m = 0;
      measurements.depth_m  = cat.default_depth_m || 1.8;
    } else if (kind === 'garage_door_double' || kind === 'garage_door_small') {
      measurements.width_m  = cat.default_width_m  || 0;
      measurements.height_m = cat.default_height_m || 0;
    }
    saveExtras([
      ...extrasPicks,
      { item_id: itemId,
        qty: extraFields.qty || 1,
        measurements,
        pin_id: extraFields.pin_id || null,
        origin: extraFields.origin || 'paint',
      },
    ]);
    setExpandedExtras(prev => new Set(prev).add(itemId));
    setAddExtraModalOpen(false);
  };

  const lines = bundle?.lines || [];
  const summary = bundle?.summary || {};
  const err = bundle?.error;
  const extraLines = bundle?.extras || [];
  // Iter314 R4 · Per-face pin split rows + round-down toggle.
  const perFace = bundle?.per_face || [];
  const roundDown = !!bundle?.round_down_to_10;
  const roofM2Source = bundle?.roof_m2_source || '';
  const [roundDownToggling, setRoundDownToggling] = useState(false);
  const onToggleRoundDown = async () => {
    setRoundDownToggling(true);
    try {
      await api.post(`/painting/appraisals/${appraisalId}/round-down`, { enabled: !roundDown });
      await load();
    } catch (e) {
      toast.error(`Round-down toggle failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRoundDownToggling(false);
    }
  };

  // v10.29 Sprint 4c (2026-02) · Slice E · Re-push overwrite reminder
  // banner. Same pattern as Reroof (Sprint 4a): permanent + dismissible
  // per-appraisal via localStorage so operators aren't nagged twice.
  const bannerKey = `recoat-repush-banner-dismissed-${appraisalId}`;
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return typeof localStorage !== 'undefined' && localStorage.getItem(bannerKey) === '1'; }
    catch { return false; }
  });
  const dismissBanner = useCallback(() => {
    try { localStorage.setItem(bannerKey, '1'); } catch { /* ignore */ }
    setBannerDismissed(true);
  }, [bannerKey]);

  // v10.29 Sprint 4c · Slice C · Fast-jump to Components tab.
  // React Router's `?tab=Components&focus_section=<wt>` URL contract.
  // ReCoat is a valid work-type key (constants.js line 43) so this
  // lands on the right accordion.
  const navigate = useNavigate();
  const jumpToComponents = useCallback((section = 'ReCoat') => {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', 'Components');
    params.set('focus_section', section);
    navigate(`?${params.toString()}`, { replace: false });
  }, [navigate]);

  return (
    <CalcShadowProvider defaultShow={true}>
    <div className="space-y-4" data-testid="painting-builder-tab">
      {/* Header */}
      <div className="bg-gradient-to-br from-sky-50 to-violet-50 border border-violet-200 rounded-md p-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-violet-900 flex items-center gap-2 font-[Manrope]">
            <Paintbrush size={18} /> Recoat Quote Builder
          </h3>
          <p className="text-xs text-violet-800/80 mt-1 max-w-2xl">
            Standalone recoat quote. Prices come from <strong>Component Rate Tables → Re-Coat</strong>
            (Materials $ + Labour $ = RTR rate). Pick a recoat system + confirm roof m²; the coats are
            the breakdown and always add up to the table rate.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="painting-refresh-btn"
          className="text-sm border border-violet-300 bg-white text-violet-800 rounded-md px-3 py-1.5 hover:bg-violet-100 flex items-center gap-1 self-start"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Iter167 — Roof Details quick-edit panel under the Painting banner. */}
      <BuilderRoofDetailsPanel api={api} appraisalId={appraisalId} onApplied={load} />

      {flash && (
        <div
          data-testid="painting-flash"
          className={`text-sm rounded-md p-3 border ${
            flash.tone === 'ok'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
          }`}
        >
          {flash.tone === 'ok' ? <CheckCircle2 size={14} className="inline mr-1" /> : <AlertTriangle size={14} className="inline mr-1" />}
          {flash.msg}
        </div>
      )}

      {/* v10.29 Sprint 4c (2026-02) · Slice E · Re-push overwrite
          reminder. Dismissible per-appraisal (localStorage). Amber tone
          so it reads informational, not alarming. Mirrors Reroof/Repairs. */}
      {!bannerDismissed && (
        <div className="px-3 py-2 rounded text-xs flex items-start gap-2 bg-amber-50 text-amber-900 ring-1 ring-amber-200"
             data-testid="painting-repush-banner">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <strong>Heads-up:</strong> Re-pushing overwrites any manual edits made to
            builder-emitted <em>coat</em> lines in the <span className="font-semibold">Components</span> tab.
            Bespoke <em>Extras</em> and <em>Labour Extras</em> saved here are preserved across re-pushes.
          </div>
          <button className="text-[11px] underline hover:no-underline"
                  onClick={dismissBanner}
                  data-testid="painting-repush-banner-dismiss">
            dismiss (this appraisal)
          </button>
        </div>
      )}

      {/* v10.29 Sprint 4c · Slice A · Two-column content layout.
          LEFT (col-span-4 on lg): variant chip bar + system dropdown +
          roof m² input — "any variants" panel per user spec.
          RIGHT (col-span-8 on lg): summary + action bar + per-face +
          coats breakdown — "breakdown on the right" per user spec.
          Extras summary + Labour Extras drop below full-width. */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid="painting-hybrid-grid">
        {/* ─── LEFT · Variants + picker ─── */}
        <div className="lg:col-span-4 space-y-3" data-testid="painting-left-column">
          <div className="bg-white border border-slate-200 rounded-md p-3" data-testid="painting-picker">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2 font-[Manrope] flex items-center gap-1.5">
              <Paintbrush size={12} /> Variants & area
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center justify-between gap-2">
                <span>Recoat system</span>
                {appraisal?.roof_subtype && (
                  <span data-testid="painting-cascade-chip" className="font-normal normal-case tracking-normal text-[10px] px-1.5 py-0 bg-violet-50 text-violet-700 border border-violet-200 rounded-full">
                    ⛁ cascade · {appraisal.roof_subtype}
                  </span>
                )}
              </label>
              <select
                data-testid="painting-system-select"
                value={paintSystemId}
                onChange={(e) => onSystemChange(e.target.value)}
                disabled={saving}
                className="w-full mt-0.5 text-sm px-2 py-1.5 border border-slate-300 rounded bg-white"
              >
                <option value="">— Select recoat system —</option>
                {paintSystems.map(p => (
                  <option key={p._id || p.id} value={p._id || p.id}>{p.system_name}</option>
                ))}
              </select>
              {paintSystems.length >= 2 && (
                <div
                  data-testid="painting-variant-chip-bar"
                  className="flex flex-wrap gap-1.5 mt-2 items-center"
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-1 w-full">
                    Quick swap
                  </span>
                  {paintSystems.map(p => {
                    const pid = p._id || p.id;
                    const isActive = pid === paintSystemId;
                    return (
                      <button
                        key={pid}
                        type="button"
                        data-testid={`painting-variant-chip-${pid}`}
                        onClick={() => onSystemChange(pid)}
                        disabled={saving}
                        title={p.system_name}
                        className={
                          isActive
                            ? "text-[11px] px-2 py-1 rounded-full border-2 border-violet-500 bg-violet-50 text-violet-800 font-semibold shadow-sm transition-all"
                            : "text-[11px] px-2 py-1 rounded-full border border-slate-300 bg-white text-slate-600 hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 transition-all"
                        }
                      >
                        {isActive && (
                          <CheckCircle2 size={11} className="inline mr-1 -mt-0.5" />
                        )}
                        {p.system_name}
                      </button>
                    );
                  })}
                </div>
              )}
              {paintSystems.length === 0 && appraisal?.roof_subtype && (
                <p data-testid="painting-no-systems" className="text-[11px] text-amber-700 mt-1">
                  No paint systems seeded for <strong>{appraisal.roof_subtype}</strong>. Add one in Admin → Roof Service Catalogue.
                </p>
              )}
            </div>
            <div className="mt-3">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center justify-between gap-2">
                <span>Roof m² (paint area)</span>
                {roofDetailsM2 > 0 && Math.abs((Number(roofM2) || 0) - roofDetailsM2) > 0.005 && (
                  <button
                    type="button"
                    data-testid="painting-sync-roof-m2"
                    onClick={() => { setRoofM2(roofDetailsM2); savePicks(paintSystemId, roofDetailsM2); }}
                    className="font-normal normal-case tracking-normal text-[10px] px-1.5 py-0 bg-sky-50 text-sky-700 hover:bg-sky-100 border border-sky-200 rounded-full transition-colors"
                    title={`Reset to the sum of every roof_details.roof_m² for this property (${roofDetailsM2.toFixed(2)} m²)`}
                  >📐 sync · {roofDetailsM2.toFixed(2)}m²</button>
                )}
                {roofDetailsM2 > 0 && Math.abs((Number(roofM2) || 0) - roofDetailsM2) <= 0.005 && (
                  <span data-testid="painting-roof-m2-auto" className="font-normal normal-case tracking-normal text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                    📐 from Roof Details
                  </span>
                )}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                data-testid="painting-roof-m2-input"
                value={roofM2 || ''}
                onChange={(e) => setRoofM2(e.target.value)}
                onBlur={onRoofM2Blur}
                placeholder={roofDetailsM2 ? roofDetailsM2.toFixed(2) : '218.46'}
                className="w-full mt-0.5 text-sm px-2 py-1.5 border border-slate-300 rounded"
              />
              <p className="text-[10px] text-slate-500 italic mt-1.5">
                Picks save automatically and propagate to the Restoration Combiner tab.
              </p>
            </div>
            <RateRowBanner meta={bundle?.meta} hasSystem={!!paintSystemId} />
            <div className="mt-3" data-testid="painting-quote-letter-block">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Quote letter</label>
              <select
                data-testid="painting-quote-letter-select"
                value={bundle?.meta?.quotation_type || ''}
                onChange={(e) => onLetterChange(e.target.value)}
                className="w-full mt-0.5 text-sm px-2 py-1.5 border border-slate-300 rounded bg-white"
              >
                <option value="" disabled>— Auto ({bundle?.meta?.suggested_quotation_type || 'Painting'}) —</option>
                {letterOptions.map(ref => <option key={ref} value={ref}>{ref}</option>)}
              </select>
              <p className="text-[10px] text-slate-500 italic mt-1">
                Used by the Quotation Letter PDF (one total price; coats + extras listed in the bullet block).
              </p>
            </div>
          </div>
        </div>

        {/* ─── RIGHT · Breakdown ─── */}
        <div className="lg:col-span-8 space-y-3" data-testid="painting-right-column">
      {loading && !bundle ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 p-6">
          <Loader2 size={14} className="animate-spin" /> Loading paint preview…
        </div>
      ) : err === 'missing-paint-system' ? (
        <div data-testid="painting-empty" className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-900">
          <AlertTriangle size={14} className="inline mr-1" />
          {bundle.message}
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="flex justify-end">
            <CalcShadowToggle testid="painting-calcs-toggle" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="painting-summary">
            <SummaryCard label="Coats" value={summary.line_count || 0} tone="slate" />
            <SummaryCard label="Roof m²" value={(summary.roof_m2 || 0).toFixed(2)} tone="sky" />
            <SummaryCard label="System" value={bundle?.meta?.paint_system_name || '—'} tone="violet" />
            <SummaryCard label="Grand total" value={`$${(summary.grand_total || 0).toFixed(2)}`} tone="emerald" big />
          </div>
          <QuoteTotalStrip api={api} appraisalId={appraisalId} linesTotal={summary.grand_total || 0} testid="painting-quote-total-strip" />
          {summary?.roof_m2_confidence && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Roof m² source</span>
              <RoofM2ConfidenceBanner api={api} appraisalId={appraisalId} source={summary.roof_m2_source} confidence={summary.roof_m2_confidence} label="Roof m²" />
            </div>
          )}

          {/* Action bar */}
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="painting-push-btn"
              onClick={onPush}
              disabled={pushing || saving || savingExtras || lines.length === 0}
              className="bg-violet-600 text-white text-sm font-semibold rounded-md px-4 py-2 hover:bg-violet-700 disabled:opacity-50 flex items-center gap-2"
            >
              {pushing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {pushing ? 'Pushing…' : 'Push to Components'}
            </button>
            <SaveDocButton
              appraisalId={appraisalId}
              docKind="painting_quotation"
              label="Quotation Letter PDF"
              expandChildren={false}
              size="sm"
              variant="outline"
              disabled={lines.length === 0 && extraLines.length === 0}
              testId="painting-pdf-btn"
              className="bg-white border border-slate-300 text-slate-700 text-sm font-semibold rounded-md px-4 py-2 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2"
            />
          </div>

          {/* Lines */}
          {lines.length === 0 && extraLines.length === 0 ? (
            <div data-testid="painting-no-lines" className="bg-slate-50 border border-slate-200 rounded-md p-4 text-sm text-slate-600">
              No paint coats yet. Pick a paint system and confirm roof m² above.
            </div>
          ) : lines.length > 0 ? (
            <>
              {/* Iter314 R4 · Per-face pin split — one card per Face pin
                  in the diagram, showing face_m2 (with optional round-down
                  applied) + per-face subtotal. Legacy area-only appraisals
                  (no face pins) skip this block entirely. */}
              {perFace.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-md p-3 space-y-3" data-testid="painting-per-face-panel">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wide text-slate-600">Per Face</span>
                      <span className="text-[10px] text-slate-500">
                        {perFace.length} face{perFace.length === 1 ? '' : 's'} · roof m² sourced from {roofM2Source === 'face_pins' ? 'diagram face pins' : roofM2Source}
                      </span>
                    </div>
                    <button
                      type="button"
                      data-testid="painting-round-down-toggle"
                      onClick={onToggleRoundDown}
                      disabled={roundDownToggling}
                      title="Round each face_m² DOWN to the nearest 10 before pricing"
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition ${
                        roundDown
                          ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
                          : 'bg-slate-50 text-slate-600 border-slate-300 hover:bg-slate-100'
                      } ${roundDownToggling ? 'opacity-60 cursor-wait' : ''}`}
                    >
                      <span className={`inline-block w-2 h-2 rounded-full ${roundDown ? 'bg-amber-500' : 'bg-slate-400'}`}></span>
                      Round face m² DOWN to 10
                    </button>
                  </div>
                  <div className="overflow-hidden rounded-md border border-slate-100">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 text-slate-700 text-xs uppercase tracking-wide">
                        <tr>
                          <th className="text-left p-2 px-3 w-16">Ref</th>
                          <th className="text-left p-2 px-3">Label</th>
                          <th className="text-right p-2 px-3 w-32">Face m²</th>
                          <th className="text-right p-2 px-3 w-20">Coats</th>
                          <th className="text-right p-2 px-3 w-28">Rate ($/m²)</th>
                          <th className="text-right p-2 px-3 w-28">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {perFace.map((f, idx) => (
                          <tr key={`per-face-${f.pin_id || idx}`} data-testid={`painting-per-face-row-${idx}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                            <td className="p-2 px-3 font-mono text-xs text-slate-500">{f.pin_ref}</td>
                            <td className="p-2 px-3 text-slate-700">{f.face_label}</td>
                            <td className="p-2 px-3 text-right">
                              {f.face_m2.toFixed(2)}
                              {roundDown && f.face_m2_raw !== f.face_m2 && (
                                <span className="ml-1 text-[10px] text-amber-700" title={`raw ${f.face_m2_raw.toFixed(2)}`}>
                                  ↓{f.face_m2_raw.toFixed(2)}
                                </span>
                              )}
                            </td>
                            <td className="p-2 px-3 text-right text-slate-500">{f.coats.length}</td>
                            <td
                              className="p-2 px-3 text-right text-slate-600 font-mono tabular-nums"
                              title="Sum of per-m² rates across all coats · same rate is applied to every face on this paint system"
                              data-testid={`painting-per-face-rate-${idx}`}
                            >
                              ${(f.combined_rate_per_m2 || 0).toFixed(2)}
                            </td>
                            <td className="p-2 px-3 text-right font-semibold">${f.subtotal.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="painting-lines-table">
              <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-600 font-[Manrope]">
                  Coat breakdown
                </div>
                {/* v10.29 Sprint 4c · Slice C · Fast-jump to Components → ReCoat section */}
                <button
                  type="button"
                  onClick={() => jumpToComponents('ReCoat')}
                  data-testid="painting-fastjump-coats"
                  title="Open the ReCoat accordion in the Components tab to edit line quantities, rates or suppliers"
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-violet-300 bg-white text-violet-700 hover:bg-violet-50 hover:border-violet-400 transition-colors"
                >
                  <ExternalLink size={11} /> Open in Components
                </button>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-900 text-white text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left p-2 px-3">Coat</th>
                    <th className="text-right p-2 px-3 w-24">Qty</th>
                    <th className="text-left p-2 px-3 w-16">Unit</th>
                    <th className="text-right p-2 px-3 w-24">Rate</th>
                    <th className="text-right p-2 px-3 w-24">Mat</th>
                    <th className="text-right p-2 px-3 w-24">Lab</th>
                    <th className="text-right p-2 px-3 w-28">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((li, idx) => (
                    <tr key={`paint-line-${idx}`} data-testid={`painting-line-${idx}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="p-2 px-3 text-slate-700">
                        {li.type_name}
                        {/* v10.29 Sprint 4c · Slice B · Rate row + Mat/Lab split visibility */}
                        {li.rate_row_ref && (
                          <div
                            data-testid={`painting-line-rate-row-${idx}`}
                            className="text-[10px] text-slate-500 mt-0.5 font-mono-num"
                            title="Rate row resolved from paint_systems catalogue"
                          >
                            ⛁ {li.rate_row_ref}
                            {li.split_source === '50-50-fallback' && (
                              <span className="ml-1 text-amber-700" title="Catalogue lacks explicit material_per_m2 + labour_per_m2 — using 50/50 fallback">
                                · 50/50 split
                              </span>
                            )}
                          </div>
                        )}
                        <CalcShadow testid={`painting-line-calc-${idx}`}>
                          {(li.quantity || 0).toFixed(2)} m² × ${(li.rate || 0).toFixed(2)}
                          {' '}(mat ${(li.materials_rate || 0).toFixed(2)} + lab ${(li.labour_rate || 0).toFixed(2)})
                          {' '}= <strong>${(li.total || 0).toFixed(2)}</strong>
                        </CalcShadow>
                      </td>
                      <td className="p-2 px-3 text-right">{(li.quantity || 0).toFixed(2)}</td>
                      <td className="p-2 px-3 text-slate-500">{li.unit || 'm²'}</td>
                      <td className="p-2 px-3 text-right">${(li.rate || 0).toFixed(2)}</td>
                      <td className="p-2 px-3 text-right text-sky-700 font-mono-num" data-testid={`painting-line-mat-${idx}`}>
                        ${(li.materials_total || 0).toFixed(2)}
                      </td>
                      <td className="p-2 px-3 text-right text-emerald-700 font-mono-num" data-testid={`painting-line-lab-${idx}`}>
                        ${(li.labour_total || 0).toFixed(2)}
                      </td>
                      <td className="p-2 px-3 text-right font-semibold">${(li.total || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr className="bg-violet-50 border-t border-violet-200">
                    <td colSpan={6} className="p-2 px-3 text-right font-bold text-violet-900">
                      {extraLines.length ? 'Paint subtotal' : 'Grand total'}
                    </td>
                    <td className="p-2 px-3 text-right font-bold text-violet-700">${(summary.paint_total ?? summary.grand_total ?? 0).toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            </>
          ) : null}
        </>
      )}
        </div>
      </div>

      {/* v10.29 Sprint 4c · Slice A · Full-width footer sections:
          Extras summary + Labour Extras. Kept below the 2-col grid
          because they're wide tables that need horizontal room. */}
      {!loading && err !== 'missing-paint-system' && (
        <>
          {/* Iter219 · v10.29 Sprint 4c Slice C — Extra Painting summary.
              Read-only display of the operator's persisted picks + fast-
              jump to the Components tab for edits. */}
          <ExtraPaintingSection
            catalog={extrasCatalog}
            picks={extrasPicks}
            extraLines={extraLines}
            saving={savingExtras}
            extrasTotal={summary.extras_total || 0}
            paintTotal={summary.paint_total || 0}
            grandTotal={summary.grand_total || 0}
            onAdd={addExtra}
            onUpdateQty={updateExtraQty}
            onUpdateMeasurements={updateExtraMeasurements}
            onRemove={removeExtra}
            expandedSet={expandedExtras}
            onToggleExpanded={toggleExtraExpanded}
            prefill={extrasPrefill}
            hasPaintLines={lines.length > 0}
            onOpenAddModal={() => setAddExtraModalOpen(true)}
          />
          {addExtraModalOpen && (
            <AddExtraLineModal
              catalog={extrasCatalog}
              facePins={(appraisal?.roof_diagram_annotations || []).filter(p => (p?.type || '').toLowerCase() === 'face')}
              onClose={() => setAddExtraModalOpen(false)}
              onConfirm={addExtraWithLink}
            />
          )}
          {/* Iter314 C2+C3 · Labour Extras section (barge / masking /
              tile replacement / custom). Labour-only, routes through
              the misc `[Labourer Charge Out p/hr]` rate. */}
          <LabourExtrasSection
            rows={labourExtras}
            resolvedRows={bundle?.labour_extras || []}
            ctx={laborCtx}
            saving={labourSaving}
            onSave={saveLabourExtras}
          />
          {/* Iter314 R4 · Round-down toggle lives in the per-face table above. */}
        </>
      )}
    </div>
    </CalcShadowProvider>
  );
}

function RateRowBanner({ meta, hasSystem }) {
  if (!hasSystem || !meta?.paint_system_id) return null;
  const rr = meta.rate_row;
  if (!rr) {
    return (
      <div data-testid="painting-rate-row-missing" className="mt-3 text-[11px] rounded p-2 bg-rose-50 border border-rose-200 text-rose-800 flex gap-1.5">
        <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
        <span>No Component Rate row linked to this paint system — using the paint-system coat rates. Add a Re-Coat row in Component Rate Tables (or run Recoat cleanup).</span>
      </div>
    );
  }
  return (
    <div data-testid="painting-rate-row-banner" className="mt-3 text-[11px] rounded p-2 bg-emerald-50 border border-emerald-200 text-emerald-900">
      <div className="font-bold uppercase tracking-wider text-[10px] text-emerald-700">Priced from Component Rate Tables</div>
      <div className="mt-0.5">
        <span className="font-semibold">{rr.name}</span>:{' '}
        <span className="font-mono-num">Mat ${rr.materials_rate.toFixed(2)} + Lab ${rr.labour_rate.toFixed(2)} = <strong data-testid="painting-rate-row-rtr">${rr.rtr_rate.toFixed(2)}</strong>/{rr.unit}</span>
      </div>
      <a href="/component-rates?component=recoat" data-testid="painting-rate-row-link" className="inline-flex items-center gap-1 mt-1 text-emerald-800 underline hover:no-underline">
        <ExternalLink size={10} /> Edit in Component Rate Tables
      </a>
    </div>
  );
}

function SummaryCard({ label, value, tone, big = false }) {
  const toneMap = {
    slate:    'bg-slate-50 border-slate-200 text-slate-800',
    sky:      'bg-sky-50 border-sky-200 text-sky-800',
    violet:   'bg-violet-50 border-violet-200 text-violet-800',
    emerald:  'bg-emerald-50 border-emerald-300 text-emerald-900',
  };
  return (
    <div className={`rounded-md border p-3 ${toneMap[tone] || toneMap.slate}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
      <div className={`mt-1 font-bold ${big ? 'text-2xl' : 'text-lg'}`} title={value}>{value}</div>
    </div>
  );
}

/**
 * ExtraPaintingSection — Iter219.
 *
 * Add-on picker for non-roof painting items (Recoat External Fascia,
 * Garage Doors, Gutters, …). Catalog is sourced from the office-level
 * `/api/extra-painting-items` collection; per-pick qty + optional
 * rate override is persisted on the appraisal via
 * PUT /painting/appraisals/{id}/extras.
 *
 * Rendered under the paint coats table. Picks roll into the same
 * grand total as the paint coats and get pushed onto Components when
 * the operator hits "Push to Components".
 */
function ExtraPaintingSection({
  catalog, picks, extraLines, saving,
  extrasTotal, paintTotal, grandTotal,
  onAdd, onUpdateQty, onUpdateMeasurements, onRemove,
  expandedSet, onToggleExpanded, prefill,
  hasPaintLines,
  onOpenAddModal,
}) {
  const [pickerValue, setPickerValue] = useState('');

  const pickedIds = new Set((picks || []).map(p => p.item_id));
  const available = (catalog || []).filter(c => !pickedIds.has(c._id || c.id));

  // Build a lookup so the qty-row can show name + unit + computed line total.
  const byId = {};
  (catalog || []).forEach(c => { byId[c._id || c.id] = c; });
  const lineById = {};
  (extraLines || []).forEach(li => { if (li.source_id) lineById[li.source_id] = li; });

  const handleAdd = () => {
    if (!pickerValue) return;
    onAdd(pickerValue);
    setPickerValue('');
  };

  return (
    <div className="bg-white border border-amber-200 rounded-md overflow-hidden" data-testid="extra-painting-section">
      <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h4 className="text-sm font-bold text-amber-900 flex items-center gap-1.5 font-[Manrope]">
            <Paintbrush size={14} /> Extra Painting Add-ons
          </h4>
          <p className="text-[11px] text-amber-800/80 mt-0.5">
            Fascia, gutters, garage doors, fences. Priced from <strong>Component Rate Tables → Re-Coat</strong>
            (Materials $ + Labour $ per each / m). Edit rates there.
          </p>
        </div>
        {saving && <Loader2 size={14} className="animate-spin text-amber-700" />}
      </div>

      {/* Picker */}
      <div className="p-3 flex items-end gap-2 border-b border-slate-100">
        <div className="flex-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Add item</label>
          <select
            data-testid="extra-painting-picker"
            value={pickerValue}
            onChange={e => setPickerValue(e.target.value)}
            disabled={available.length === 0}
            className="w-full mt-0.5 text-sm px-2 py-1.5 border border-slate-300 rounded bg-white disabled:bg-slate-50 disabled:text-slate-400"
          >
            <option value="">— Pick an Extra Painting item —</option>
            {available.map(c => {
              const id = c._id || c.id;
              const sell = (Number(c.materials_rate) || 0) + (Number(c.labour_rate) || 0);
              return (
                <option key={id} value={id}>
                  {c.name} — ${sell.toFixed(2)}/{c.unit || 'lm'}
                </option>
              );
            })}
          </select>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={!pickerValue}
          data-testid="extra-painting-add-btn"
          className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-semibold rounded px-3 py-1.5 flex items-center gap-1"
        >
          <Plus size={14} /> Add
        </button>
        {/* Iter314 R5 · Alternative entry that opens a sub-group cascade
            modal — lets operators pick a garage-door / fascia extra AND
            link it to a specific face pin in one flow. */}
        {onOpenAddModal && (
          <button
            type="button"
            onClick={onOpenAddModal}
            data-testid="extra-painting-open-modal-btn"
            title="Open picker with face-pin link"
            className="border border-amber-400 text-amber-800 hover:bg-amber-50 text-xs font-semibold rounded px-3 py-1.5 flex items-center gap-1"
          >
            <Plus size={12} /> Add + Link
          </button>
        )}
      </div>

      {/* Picks */}
      {(picks || []).length === 0 ? (
        <div className="p-3 text-xs text-slate-500 italic" data-testid="extra-painting-empty">
          {catalog && catalog.length === 0
            ? 'No /each or /lm rows in Component Rate Tables → Re-Coat yet.'
            : 'No extras picked. Use the dropdown above to add fascia, gutters or garage doors.'}
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="extra-painting-table">
          <thead className="bg-slate-900 text-white text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left p-2 px-3">Item</th>
              <th className="text-right p-2 px-3 w-24">Qty</th>
              <th className="text-left p-2 px-3 w-16">Unit</th>
              <th className="text-right p-2 px-3 w-24">Rate</th>
              <th className="text-right p-2 px-3 w-28">Total</th>
              <th className="p-2 px-3 w-10"> </th>
            </tr>
          </thead>
          <tbody>
            {picks.map((p, idx) => {
              const cat = byId[p.item_id];
              const li  = lineById[p.item_id];
              const name = cat?.name || (li?.component) || 'Removed item';
              const unit = li?.unit || cat?.unit || 'lm';
              const rate = li?.rate ?? ((Number(cat?.materials_rate) || 0) + (Number(cat?.labour_rate) || 0));
              const total = li?.total ?? (Number(p.qty || 0) * rate);
              const missing = !cat;
              const kind = (cat?.kind || 'generic').toLowerCase();
              const hasEditor = kind !== 'generic';
              const qtyFromLength = ['fascia', 'gutter', 'fence'].includes(kind);
              const isExpanded = expandedSet?.has(p.item_id);
              const m = p.measurements || {};
              return (
                <React.Fragment key={p.item_id}>
                  <tr data-testid={`extra-painting-row-${idx}`}
                      className={`border-t border-slate-100 hover:bg-slate-50/50 ${missing ? 'bg-rose-50/40' : ''}`}>
                    <td className="p-2 px-3 text-slate-700">
                      {hasEditor && (
                        <button
                          type="button"
                          onClick={() => onToggleExpanded?.(p.item_id)}
                          data-testid={`extra-painting-expand-${idx}`}
                          className="mr-1.5 inline-flex w-5 h-5 items-center justify-center text-slate-500 hover:text-slate-800 align-middle"
                          title={isExpanded ? 'Collapse detail editor' : 'Edit measurements'}
                        >{isExpanded ? '▾' : '▸'}</button>
                      )}
                      {name}
                      {missing && <span className="ml-2 text-[10px] text-rose-700 font-semibold">(removed from catalog)</span>}
                      {cat?.supplier && !missing && (
                        <span className="ml-2 text-[10px] px-1.5 py-0 bg-slate-100 border border-slate-200 rounded text-slate-600">
                          {cat.supplier}
                        </span>
                      )}
                      {/* Iter231 — show the cascade math summary on the row when measurements are present */}
                      {li?.notes && (
                        <div className="text-[10px] text-slate-500 mt-0.5 font-mono-num">{li.notes}</div>
                      )}
                    </td>
                    <td className="p-2 px-3 text-right">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={qtyFromLength ? (li?.qty ?? m.length_m ?? p.qty) : p.qty}
                        onChange={e => onUpdateQty(p.item_id, Number(e.target.value) || 0)}
                        data-testid={`extra-painting-qty-${idx}`}
                        disabled={qtyFromLength}
                        title={qtyFromLength ? 'Qty = length (m) — open the editor to change.' : undefined}
                        className={`w-20 text-right px-1.5 py-1 border rounded text-sm ${qtyFromLength ? 'border-slate-200 bg-slate-50 text-slate-400' : 'border-slate-300'}`}
                      />
                    </td>
                    <td className="p-2 px-3 text-slate-500">{unit}</td>
                    <td className="p-2 px-3 text-right">${(rate || 0).toFixed(2)}</td>
                    <td className="p-2 px-3 text-right font-semibold">${(total || 0).toFixed(2)}</td>
                    <td className="p-2 px-3 text-right">
                      <button
                        type="button"
                        onClick={() => onRemove(p.item_id)}
                        data-testid={`extra-painting-remove-${idx}`}
                        className="text-rose-600 hover:text-rose-800"
                        title="Remove this extra"
                      ><Trash2 size={14} /></button>
                    </td>
                  </tr>
                  {/* Iter231 (A5027 Issue 2) — Collapsible per-pick
                      detail editor. Renders fascia / gutter (length ×
                      depth) or garage door (W × H) inputs based on
                      catalogue `kind`. Paint+labour rates shown read-
                      only — operator edits them in the admin
                      catalogue, not per-quote. */}
                  {isExpanded && hasEditor && (
                    <tr data-testid={`extra-painting-editor-${idx}`} className="border-t border-amber-100 bg-amber-50/40">
                      <td colSpan={6} className="p-3 px-6">
                        <div className="grid grid-cols-12 gap-3 text-xs">
                          {(kind === 'fascia' || kind === 'gutter' || kind === 'fence') && (
                            <>
                              <div className="col-span-3">
                                <label className="block text-[10px] uppercase font-bold tracking-wider text-slate-500">
                                  Length (m)
                                  {kind === 'fascia' && prefill?.fascia_total_m != null && (
                                    <span className="ml-1 text-[9px] text-emerald-700 normal-case">
                                      auto · {prefill.fascia_total_m}m (gutter + barge)
                                    </span>
                                  )}
                                  {kind === 'gutter' && prefill?.gutter_total_m != null && (
                                    <span className="ml-1 text-[9px] text-emerald-700 normal-case">
                                      auto · {prefill.gutter_total_m}m
                                    </span>
                                  )}
                                </label>
                                <input
                                  type="number" min="0" step="0.01"
                                  value={m.length_m ?? ''}
                                  onChange={e => onUpdateMeasurements?.(p.item_id, { length_m: e.target.value })}
                                  data-testid={`extra-painting-length-${idx}`}
                                  className="w-full mt-1 px-2 py-1 border border-slate-300 rounded text-sm font-mono-num"
                                />
                              </div>
                              <div className="col-span-3">
                                <label className="block text-[10px] uppercase font-bold tracking-wider text-slate-500">
                                  Depth / face height (m)
                                </label>
                                <input
                                  type="number" min="0" step="0.001"
                                  value={m.depth_m ?? ''}
                                  onChange={e => onUpdateMeasurements?.(p.item_id, { depth_m: e.target.value })}
                                  data-testid={`extra-painting-depth-${idx}`}
                                  className="w-full mt-1 px-2 py-1 border border-slate-300 rounded text-sm font-mono-num"
                                />
                              </div>
                            </>
                          )}
                          {(kind === 'garage_door_double' || kind === 'garage_door_small') && (
                            <>
                              <div className="col-span-3">
                                <label className="block text-[10px] uppercase font-bold tracking-wider text-slate-500">Width (m)</label>
                                <input
                                  type="number" min="0" step="0.01"
                                  value={m.width_m ?? ''}
                                  onChange={e => onUpdateMeasurements?.(p.item_id, { width_m: e.target.value })}
                                  data-testid={`extra-painting-width-${idx}`}
                                  className="w-full mt-1 px-2 py-1 border border-slate-300 rounded text-sm font-mono-num"
                                />
                              </div>
                              <div className="col-span-3">
                                <label className="block text-[10px] uppercase font-bold tracking-wider text-slate-500">Height (m)</label>
                                <input
                                  type="number" min="0" step="0.01"
                                  value={m.height_m ?? ''}
                                  onChange={e => onUpdateMeasurements?.(p.item_id, { height_m: e.target.value })}
                                  data-testid={`extra-painting-height-${idx}`}
                                  className="w-full mt-1 px-2 py-1 border border-slate-300 rounded text-sm font-mono-num"
                                />
                              </div>
                            </>
                          )}
                          {/* Rates panel (read-only catalogue values) */}
                          <div className="col-span-6 grid grid-cols-3 gap-2 text-[10px] text-slate-600 bg-white ring-1 ring-amber-200 rounded p-2" data-testid={`extra-painting-rates-${idx}`}>
                            <div>
                              <div className="font-bold uppercase tracking-wider text-slate-500">Materials $</div>
                              <div className="text-base font-mono-num">${(Number(cat?.materials_rate) || 0).toFixed(2)}</div>
                            </div>
                            <div>
                              <div className="font-bold uppercase tracking-wider text-slate-500">Labour $</div>
                              <div className="text-base font-mono-num">${(Number(cat?.labour_rate) || 0).toFixed(2)}</div>
                            </div>
                            <div>
                              <div className="font-bold uppercase tracking-wider text-slate-500">RTR rate /{cat?.unit || 'unit'}</div>
                              <div className="text-base font-mono-num">${(Number(cat?.rtr_rate) || 0).toFixed(2)}</div>
                            </div>
                            <div className="col-span-3 text-[10px] text-amber-800 border-t border-amber-200 pt-1 mt-1 font-mono-num">
                              <strong>${(Number(cat?.rate_per_m2) || 0).toFixed(2)}/m²</strong> (RTR ÷ {cat?.m2_per_unit || 1} m² per {cat?.unit || 'unit'})
                              {' '}— priced from Component Rate Tables → Re-Coat
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {/* Totals footer */}
            <tr className="bg-amber-50 border-t border-amber-200">
              <td colSpan={4} className="p-2 px-3 text-right font-bold text-amber-900">
                Extras subtotal
              </td>
              <td className="p-2 px-3 text-right font-bold text-amber-700" data-testid="extra-painting-subtotal">
                ${(extrasTotal || 0).toFixed(2)}
              </td>
              <td></td>
            </tr>
            {hasPaintLines && (
              <tr className="bg-violet-50 border-t border-violet-200">
                <td colSpan={4} className="p-2 px-3 text-right font-bold text-violet-900">
                  Grand total (paint ${(paintTotal || 0).toFixed(2)} + extras ${(extrasTotal || 0).toFixed(2)})
                </td>
                <td className="p-2 px-3 text-right font-bold text-violet-700" data-testid="extra-painting-grand-total">
                  ${(grandTotal || 0).toFixed(2)}
                </td>
                <td></td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}


/**
 * AddExtraLineModal · Iter314 R5.
 *
 * Sub-group cascade + face-pin linker for Extra Painting items. Renders
 * as a centered modal overlay:
 *
 *   1. Sub-group chip strip (Fascia / Gutters / Garage Doors) — derived
 *      from each catalog item's `kind` field. Chip click filters items.
 *   2. Item radio-list (filtered by sub-group). Each row shows name +
 *      unit + sell rate.
 *   3. Qty input (default 1).
 *   4. Face-pin dropdown — populated from the current appraisal's
 *      `roof_diagram_annotations`. "— No pin (unlinked) —" is the
 *      default so the modal degrades gracefully on legacy area-only
 *      appraisals.
 *   5. Add Line + Cancel buttons.
 *
 * On Add, calls `onConfirm(itemId, { qty, pin_id, origin: 'paint' })`.
 * The parent's `addExtraWithLink` handles persistence via saveExtras.
 */
function AddExtraLineModal({ catalog, facePins, onClose, onConfirm }) {
  const [subGroup, setSubGroup] = useState('all');
  const [selectedItem, setSelectedItem] = useState('');
  const [qty, setQty] = useState(1);
  const [pinId, setPinId] = useState('');

  // Derive sub-group buckets from catalog `kind` field.
  const kindToGroup = (kind) => {
    if (!kind) return 'Other';
    if (kind === 'fascia') return 'Fascia';
    if (kind === 'gutter') return 'Gutters';
    if (kind.startsWith('garage_door')) return 'Garage Doors';
    return 'Other';
  };
  const groups = Array.from(new Set((catalog || []).map(c => kindToGroup(c.kind))));

  const filteredCatalog = (catalog || []).filter(c =>
    subGroup === 'all' ? true : kindToGroup(c.kind) === subGroup
  );

  const handleAdd = () => {
    if (!selectedItem) return;
    onConfirm(selectedItem, {
      qty: Number(qty) || 1,
      pin_id: pinId || null,
      origin: 'paint',
    });
  };

  return (
    <div
      data-testid="add-extra-line-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-amber-900 font-[Manrope]">Add Extra Line</h3>
          <button
            type="button"
            onClick={onClose}
            data-testid="add-extra-line-modal-close"
            className="text-slate-500 hover:text-slate-800 text-lg leading-none px-2"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Sub-group chip strip */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-600 block mb-1">Sub-group</label>
            <div className="flex flex-wrap gap-1.5" data-testid="add-extra-line-subgroups">
              {['all', ...groups].map(g => (
                <button
                  key={g}
                  type="button"
                  data-testid={`add-extra-line-subgroup-${g.toLowerCase().replace(/\s+/g,'-')}`}
                  onClick={() => { setSubGroup(g); setSelectedItem(''); }}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${
                    subGroup === g
                      ? 'bg-amber-600 text-white border-amber-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {g === 'all' ? 'All' : g}
                </button>
              ))}
            </div>
          </div>

          {/* Item list */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-600 block mb-1">Item</label>
            {filteredCatalog.length === 0 ? (
              <div className="text-xs text-slate-500 italic p-2 bg-slate-50 rounded" data-testid="add-extra-line-empty">
                No items in this sub-group.
              </div>
            ) : (
              <div className="space-y-1 max-h-56 overflow-y-auto border border-slate-200 rounded" data-testid="add-extra-line-items">
                {filteredCatalog.map(c => {
                  const id = c._id || c.id;
                  const kind = c.kind || 'generic';
                  const perM2 = Number(c.rate_per_m2) || 0;
                  // Iter314 R5 — modal preview matches persist semantics:
                  // `addExtraWithLink` auto-seeds default measurements for
                  // fascia / gutter / garage_door_*, which routes the row
                  // to the area-based math path in `compute_extras_lines`.
                  // Show the effective m²-based total so the operator
                  // isn't surprised when the persisted line differs from
                  // the "$X/each" chip. Falls back to (materials + labour)
                  // for non-area kinds.
                  let previewLabel;
                  if (kind === 'fascia') {
                    const areaM2 = (c.default_depth_m || 0.18);
                    previewLabel = `~$${(perM2 * areaM2).toFixed(2)}/lm (area-based)`;
                  } else if (kind === 'gutter' || kind === 'fence') {
                    const areaM2 = (c.default_depth_m || 0.12);
                    previewLabel = `~$${(perM2 * areaM2).toFixed(2)}/lm (area-based)`;
                  } else if (kind === 'garage_door_double' || kind === 'garage_door_small') {
                    const w = c.default_width_m  || 0;
                    const h = c.default_height_m || 0;
                    const areaM2 = w * h;
                    if (areaM2 > 0) {
                      previewLabel = `~$${(perM2 * areaM2).toFixed(2)}/each (${areaM2.toFixed(2)}m² × $${perM2.toFixed(2)}/m²)`;
                    } else {
                      const sell = (Number(c.materials_rate) || 0) + (Number(c.labour_rate) || 0);
                      previewLabel = `$${sell.toFixed(2)}/each`;
                    }
                  } else {
                    const sell = (Number(c.materials_rate) || 0) + (Number(c.labour_rate) || 0);
                    previewLabel = `$${sell.toFixed(2)}/${c.unit || 'lm'}`;
                  }
                  const selected = selectedItem === id;
                  return (
                    <label
                      key={id}
                      data-testid={`add-extra-line-item-${id}`}
                      className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer border-l-4 transition ${
                        selected
                          ? 'bg-amber-50 border-amber-500'
                          : 'bg-white border-transparent hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="add-extra-item"
                        value={id}
                        checked={selected}
                        onChange={() => setSelectedItem(id)}
                        className="w-3.5 h-3.5"
                      />
                      <span className="flex-1">{c.name}</span>
                      <span className="text-xs text-slate-500">{previewLabel}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Qty + Pin link — side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-600 block mb-1">Qty</label>
              <input
                type="number"
                min={0}
                step={0.5}
                value={qty}
                data-testid="add-extra-line-qty"
                onChange={e => setQty(e.target.value)}
                className="w-full text-sm px-2 py-1.5 border border-slate-300 rounded"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-600 block mb-1">Link to face pin</label>
              <select
                value={pinId}
                data-testid="add-extra-line-pin-link"
                onChange={e => setPinId(e.target.value)}
                disabled={!facePins || facePins.length === 0}
                className="w-full text-sm px-2 py-1.5 border border-slate-300 rounded bg-white disabled:bg-slate-50 disabled:text-slate-400"
              >
                <option value="">— No pin (unlinked) —</option>
                {(facePins || []).map(p => (
                  <option key={p.id || p.ref} value={p.id}>
                    {p.ref} · {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="add-extra-line-cancel"
            className="text-sm text-slate-700 hover:text-slate-900 px-3 py-1.5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAdd}
            disabled={!selectedItem}
            data-testid="add-extra-line-confirm"
            className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-semibold rounded px-4 py-1.5"
          >
            Add Line
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * LabourExtrasSection · Iter314 C2 + C3.
 *
 * Simple labour-only add-ons for the Recoat quote: barge painting,
 * masking, tile replacement, custom. Each row is:
 *   type dropdown → qty input → speed input → auto hours → auto $ →
 *   auto verbose tag note → delete
 * Math: hours = (qty × coats_multiplier) ÷ speed; $ = hours × misc rate.
 */
function LabourExtrasSection({ rows, resolvedRows, ctx, saving, onSave }) {
  const [localRows, setLocalRows] = useState(rows || []);
  useEffect(() => {
    setLocalRows(rows || []);
  }, [rows]);

  const labourerRate = ctx?.labourer_rate || 0;
  const rateOptions = ctx?.rate_options || [];
  const bargeLmTotal = ctx?.barge_lm_total || 0;
  const typeDefaults = ctx?.type_defaults || {};

  const saveTimerRef = React.useRef(null);
  const scheduleSave = useCallback((next) => {
    setLocalRows(next);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => onSave(next), 500);
  }, [onSave]);

  const addRow = (type) => {
    const defaults = typeDefaults[type] || {};
    const seedQty = type === 'barge_painting' ? bargeLmTotal : 0;
    scheduleSave([
      ...localRows,
      { type, qty: seedQty, speed: defaults.speed || 0 },
    ]);
  };
  const updateRow = (idx, patch) => {
    scheduleSave(localRows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };
  const removeRow = (idx) => {
    scheduleSave(localRows.filter((_, i) => i !== idx));
  };

  const totalDollar = (resolvedRows || []).reduce((s, r) => s + (r.total || 0), 0);
  const noRate = labourerRate <= 0;

  return (
    <div data-testid="labour-extras-section" className="bg-white border border-sky-200 rounded-md overflow-hidden">
      <div className="px-3 py-2 bg-sky-50 border-b border-sky-200 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h4 className="text-sm font-bold text-sky-900 flex items-center gap-1.5 font-[Manrope]">
            <Paintbrush size={14} /> Labour Extras
            <span className="ml-1 text-[10px] font-semibold px-1.5 py-0 rounded-full bg-white border border-sky-300 text-sky-800 uppercase tracking-wider">
              Editable
            </span>
          </h4>
          <p className="text-[11px] text-sky-800/80 mt-0.5">
            Labour-only add-ons (barge, masking, tile replacement, custom hrs). Barge qty auto-pulls from diagram ({bargeLmTotal.toFixed(2)} lm).
          </p>
        </div>
        {saving && <Loader2 size={14} className="animate-spin text-sky-700" />}
      </div>

      {/* v10.29 Sprint 4c · Slice D · Rate-row visibility banner.
          Shows the resolved misc labourer rate + points to the seed
          location when the rate is $0. */}
      <div
        data-testid="labour-extras-rate-banner"
        className={`px-3 py-2 border-b text-[11px] flex items-center gap-2 ${
          noRate
            ? 'bg-red-50 border-red-200 text-red-800'
            : 'bg-slate-50 border-slate-200 text-slate-700'
        }`}
      >
        {noRate ? <AlertTriangle size={12} /> : <Info size={12} className="text-sky-600" />}
        <span>
          <strong>Rate rows:</strong> <span className="font-mono-num">Component Rates → Misc</span> (Materials $ + Labour $). Default <strong className="font-mono-num">Labourer Charge Out ${labourerRate.toFixed(2)}/hr</strong> — pick a Tradesman rate per row below.
          {noRate && <span className="ml-1 font-semibold">· seed in Rate Tables → Misc before pricing</span>}
        </span>
      </div>

      {localRows.length === 0 ? (
        <div className="p-3 text-xs text-slate-500 italic" data-testid="labour-extras-empty">
          No labour extras. Use the buttons below to add one.
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="labour-extras-table">
          <thead className="bg-slate-900 text-white text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left p-2 px-3 w-40">Type</th>
              <th className="text-left p-2 px-3 w-52">Rate</th>
              <th className="text-right p-2 px-3 w-24">Qty</th>
              <th className="text-left p-2 px-3 w-14">Unit</th>
              <th className="text-right p-2 px-3 w-24">Speed</th>
              <th className="text-right p-2 px-3 w-16">Hrs</th>
              <th className="text-right p-2 px-3 w-24">Total</th>
              <th className="text-left p-2 px-3">Tag note</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {localRows.map((r, idx) => {
              const type = r.type || 'custom';
              const defaults = typeDefaults[type] || {};
              const resolved = (resolvedRows || [])[idx] || {};
              const unit = defaults.unit || 'hrs';
              return (
                <tr key={`labour-extra-${idx}`} data-testid={`labour-extra-row-${idx}`} className="border-t border-slate-100">
                  <td className="p-2 px-3">
                    <select
                      value={type}
                      data-testid={`labour-extra-type-${idx}`}
                      onChange={e => {
                        const nt = e.target.value;
                        const nd = typeDefaults[nt] || {};
                        updateRow(idx, {
                          type: nt,
                          speed: nd.speed || 0,
                          qty: nt === 'barge_painting' ? bargeLmTotal : (r.qty || 0),
                        });
                      }}
                      className="w-full text-xs px-1.5 py-1 border border-slate-300 rounded bg-white"
                    >
                      <option value="barge_painting">Barge Painting</option>
                      <option value="masking">Masking</option>
                      <option value="tile_replacement">Tile Replacement</option>
                      <option value="custom">Custom</option>
                    </select>
                  </td>
                  <td className="p-2 px-3">
                    <select
                      value={r.rate_row || 'Labourer Charge Out p/hr'}
                      data-testid={`labour-extra-rate-${idx}`}
                      onChange={e => updateRow(idx, { rate_row: e.target.value })}
                      className="w-full text-xs px-1.5 py-1 border border-slate-300 rounded bg-white"
                    >
                      {rateOptions.map(o => (
                        <option key={o.name} value={o.name}>{o.name.replace(' p/hr', '')} — ${o.rate.toFixed(2)}/hr</option>
                      ))}
                    </select>
                  </td>
                  <td className="p-2 px-3 text-right">
                    <input type="number" min={0} step={0.5} value={r.qty || 0}
                      data-testid={`labour-extra-qty-${idx}`}
                      onChange={e => updateRow(idx, { qty: Number(e.target.value) })}
                      className="w-full text-xs px-1.5 py-1 border border-slate-300 rounded text-right" />
                  </td>
                  <td className="p-2 px-3 text-xs text-slate-500">{unit}</td>
                  <td className="p-2 px-3 text-right">
                    <input type="number" min={0} step={0.5} value={r.speed || 0}
                      data-testid={`labour-extra-speed-${idx}`} title={`${unit}/hr`}
                      onChange={e => updateRow(idx, { speed: Number(e.target.value) })}
                      className="w-full text-xs px-1.5 py-1 border border-slate-300 rounded text-right" />
                  </td>
                  <td className="p-2 px-3 text-right text-slate-500 font-mono tabular-nums">
                    {resolved.hours != null ? resolved.hours.toFixed(2) : '—'}
                  </td>
                  <td className="p-2 px-3 text-right font-semibold" data-testid={`labour-extra-total-${idx}`}>
                    {resolved.total != null ? `$${resolved.total.toFixed(2)}` : '—'}
                  </td>
                  <td className="p-2 px-3 text-xs text-slate-500 italic" title={resolved.tag_note || ''}>
                    {resolved.tag_note || <span className="text-slate-300">(fill qty + speed)</span>}
                    {/* v10.29 Sprint 4c · Slice D · Per-row calc shadow */}
                    {resolved.hours != null && (
                      <CalcShadow testid={`labour-extra-calc-${idx}`}>
                        ({(r.qty || 0).toFixed(2)} {unit} × {(resolved.coats_multiplier || 1)} ÷ {(r.speed || 0).toFixed(2)}) = {resolved.hours.toFixed(2)} hrs × ${(resolved.labourer_rate ?? labourerRate).toFixed(2)}/hr = <strong>${(resolved.total || 0).toFixed(2)}</strong>
                      </CalcShadow>
                    )}
                  </td>
                  <td className="p-2 px-1 text-center">
                    <button type="button" onClick={() => removeRow(idx)}
                      data-testid={`labour-extra-remove-${idx}`}
                      className="text-red-500 hover:text-red-700" aria-label="Remove row">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-300 bg-sky-50">
              <td colSpan={6} className="p-2 px-3 text-right text-xs font-semibold text-slate-700">
                Labour extras total
              </td>
              <td className="p-2 px-3 text-right font-bold text-sky-900" data-testid="labour-extras-grand-total">
                ${totalDollar.toFixed(2)}
              </td>
              <td colSpan={2}></td>
            </tr>
          </tfoot>
        </table>
      )}

      <div className="px-3 py-2 border-t border-slate-100 flex flex-wrap gap-1.5 bg-slate-50">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mr-1 self-center">Add row:</span>
        <button type="button" onClick={() => addRow('barge_painting')}
          data-testid="labour-extra-add-barge"
          className="text-xs bg-white border border-sky-300 text-sky-800 hover:bg-sky-50 rounded px-2 py-1">
          + Barge{bargeLmTotal > 0 ? ` (${bargeLmTotal.toFixed(1)} lm)` : ''}
        </button>
        <button type="button" onClick={() => addRow('masking')}
          data-testid="labour-extra-add-masking"
          className="text-xs bg-white border border-sky-300 text-sky-800 hover:bg-sky-50 rounded px-2 py-1">
          + Masking
        </button>
        <button type="button" onClick={() => addRow('tile_replacement')}
          data-testid="labour-extra-add-tiles"
          className="text-xs bg-white border border-sky-300 text-sky-800 hover:bg-sky-50 rounded px-2 py-1">
          + Tile Replacement
        </button>
        <button type="button" onClick={() => addRow('custom')}
          data-testid="labour-extra-add-custom"
          className="text-xs bg-white border border-sky-300 text-sky-800 hover:bg-sky-50 rounded px-2 py-1">
          + Custom
        </button>
      </div>
    </div>
  );
}

